import type { CevroMessengerConfig, Message, QuickAction, Agent, LocaleStrings } from '../types';
import { createElement, generateId } from '../utils/dom';
import { generateAcceptAttribute } from '../utils/file-validation';
import { defaultStrings, getStrings } from '../i18n/strings';
import { MessageList } from './MessageList';

export interface ChatWindowCallbacks {
  onClose: () => void;
  onSendMessage: (message: string) => void;
  onQuickAction: (action: QuickAction) => void;
  onFileUpload?: (file: File) => void;
  onCSATSubmit?: (rating: number, feedback?: string) => Promise<void>;
  onStartNewChat?: () => void;
  onPreChatSubmit?: (data: { firstName: string; email: string }) => void;
  onShowHistory?: () => void;
}

/**
 * Chat window component
 */
export class ChatWindow {
  private config: CevroMessengerConfig;
  private callbacks: ChatWindowCallbacks;
  private element: HTMLElement | null = null;
  private messageList: MessageList;
  private inputElement: HTMLTextAreaElement | null = null;
  private sendButton: HTMLButtonElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private fileButton: HTMLButtonElement | null = null;
  private isVisible = false;
  private logoElement: HTMLElement | null = null;
  private brandNameElement: HTMLElement | null = null;
  private headerSubtitleElement: HTMLElement | null = null;
  private inputWrapper: HTMLElement | null = null;
  private closedFooter: HTMLElement | null = null;
  private csatContainer: HTMLElement | null = null;

  // Localization
  private strings: LocaleStrings = defaultStrings;

  // CSAT state
  private csatRating: number = 0;
  private csatFeedback: string = '';
  private csatSubmitting: boolean = false;

  // Pre-chat form state
  private preChatContainer: HTMLElement | null = null;
  private preChatCompleted: boolean = false;

  private customLogoUrl?: string;

  constructor(
    config: CevroMessengerConfig,
    callbacks: ChatWindowCallbacks,
    strings?: LocaleStrings,
    customLogoUrl?: string
  ) {
    this.config = config;
    this.callbacks = callbacks;
    this.strings = strings || getStrings(config.locale);
    this.customLogoUrl = customLogoUrl;
    this.messageList = new MessageList();
    this.messageList.setQuickActionHandler((action) => {
      this.callbacks.onQuickAction(action);
    });
  }

  /**
   * Update locale strings (e.g., after receiving from backend)
   */
  setStrings(backendStrings?: Partial<LocaleStrings>): void {
    this.strings = getStrings(this.config.locale, backendStrings);
    this.updateUIStrings();
  }

  /**
   * Update UI elements with current strings
   */
  private updateUIStrings(): void {
    if (this.headerSubtitleElement) {
      this.headerSubtitleElement.textContent = this.strings.headerSubtitle;
    }
    if (this.inputElement) {
      this.inputElement.placeholder = this.strings.inputPlaceholder;
    }
  }

  /**
   * Render the chat window
   */
  render(): HTMLElement {
    if (this.element) {
      return this.element;
    }

    // Main container
    this.element = createElement('div', {
      className: 'cevro-chat-window',
      id: generateId(),
    });

    // Apply positioning
    this.applyPositioning();

    // Header
    const header = this.createHeader();
    this.element.appendChild(header);

    // Messages area
    const messagesArea = this.messageList.render();
    this.element.appendChild(messagesArea);

    // Input area
    this.inputWrapper = this.createInputArea();
    this.element.appendChild(this.inputWrapper);

    return this.element;
  }

  /**
   * Apply positioning styles
   */
  private applyPositioning(): void {
    if (!this.element) return;

    const alignment = this.config.alignment || 'right';
    const vPadding = this.config.verticalPadding ?? 20;
    const hPadding = this.config.horizontalPadding ?? 20;

    // Position above launcher
    this.element.style.bottom = `${vPadding + 70}px`;

    if (alignment === 'left') {
      this.element.style.left = `${hPadding}px`;
      this.element.style.right = 'auto';
    } else {
      this.element.style.right = `${hPadding}px`;
      this.element.style.left = 'auto';
    }

    if (this.config.zIndex) {
      this.element.style.zIndex = String(this.config.zIndex - 1);
    }
  }

  /**
   * Create header element
   */
  private createHeader(): HTMLElement {
    const header = createElement('div', {
      className: 'cevro-chat-window__header',
    });

    this.logoElement = createElement('div', {
      className: 'cevro-chat-window__logo',
      'data-testid': 'company-logo',
    });
    if (this.customLogoUrl) {
      const logoImg = createElement('img', {
        src: this.customLogoUrl,
        alt: 'Logo',
        className: 'cevro-chat-window__logo-img',
      }) as HTMLImageElement;
      logoImg.style.width = '40px';
      logoImg.style.height = '40px';
      logoImg.style.objectFit = 'contain';
      logoImg.style.borderRadius = '8px';
      this.logoElement.appendChild(logoImg);
    } else {
      this.logoElement.innerHTML = this.createCompanyLogoSvg();
    }

    // History button (back chevron on the left)
    const historyButton = createElement('button', {
      className: 'cevro-chat-window__history-button',
      'aria-label': 'Conversation history',
      'data-testid': 'history-button',
    });
    historyButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    `;
    historyButton.addEventListener('click', () => {
      if (this.callbacks.onShowHistory) {
        this.callbacks.onShowHistory();
      }
    });
    header.appendChild(historyButton);

    header.appendChild(this.logoElement);

    // Title section
    const titleSection = createElement('div', {
      className: 'cevro-chat-window__title-section',
    });

    this.brandNameElement = createElement('h2', {
      className: 'cevro-chat-window__title',
      'data-testid': 'brand-name',
    }, [this.strings.headerTitle]);

    this.headerSubtitleElement = createElement('p', {
      className: 'cevro-chat-window__subtitle',
    }, [this.strings.headerSubtitle]);

    titleSection.appendChild(this.brandNameElement);
    titleSection.appendChild(this.headerSubtitleElement);
    header.appendChild(titleSection);

    // Close button
    const closeButton = createElement('button', {
      className: 'cevro-chat-window__close',
      'aria-label': 'Close chat',
      'data-testid': 'close-button',
    });
    closeButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    `;
    closeButton.addEventListener('click', () => {
      this.callbacks.onClose();
    });
    header.appendChild(closeButton);

    return header;
  }

  /**
   * Create company logo SVG
   */
  private createCompanyLogoSvg(): string {
    return `
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="40" rx="8" fill="url(#cevro-logo-gradient)"/>
        <svg x="6" y="6" width="28" height="28" viewBox="0 0 512 498">
          <g>
            <path fill="#FFFFFF" d="M298.97,91.19l-32.23-32.23c-6.12-6.12-6.12-16.03,0-22.15l32.23-32.23c6.12-6.12,16.03-6.12,22.15,0
              l30.06,30.06c2.94,2.94,6.92,4.59,11.07,4.59h48.31v17.32h-48.31c-4.15,0-8.14,1.65-11.07,4.59l-30.05,30.06
              C315,97.3,305.09,97.31,298.97,91.19z"/>
            <path fill="#FFFFFF" d="M260.84,157h96.5c4.15,0,8.14,1.65,11.07,4.59l30.05,30.05c6.12,6.12,16.03,6.12,22.15,0l30.06-30.06
              c2.94-2.94,6.92-4.59,11.07-4.59h50.21v-17.32h-50.21c-4.15,0-8.14-1.65-11.07-4.59l-30.06-30.06
              c-6.12-6.12-16.03-6.12-22.15,0l-30.06,30.05c-2.94,2.94-6.92,4.59-11.07,4.59h-96.5c-4.15,0-8.14-1.65-11.07-4.59
              l-30.06-30.05c-6.12-6.12-16.03-6.12-22.15,0l-32.23,32.23c-6.12,6.12-6.12,16.03,0,22.15l32.23,32.23
              c6.12,6.12,16.03,6.12,22.15,0l30.06-30.05C252.7,158.65,256.69,157,260.84,157z"/>
            <path fill="#FFFFFF" d="M260.84,340.58h96.5c4.15,0,8.14-1.65,11.07-4.59l30.06-30.06c6.12-6.12,16.03-6.12,22.15,0L450.67,336
              c2.94,2.94,6.92,4.59,11.07,4.59h50.21v17.32h-50.21c-4.15,0-8.14,1.65-11.07,4.59l-30.05,30.06
              c-6.12,6.12-16.03,6.12-22.15,0l-30.06-30.06c-2.94-2.94-6.92-4.59-11.07-4.59h-96.5c-4.15,0-8.14,1.65-11.07,4.59
              l-30.05,30.06c-6.12,6.12-16.03,6.12-22.15,0l-32.23-32.23c-6.12-6.12-6.12-16.03,0-22.15l32.23-32.23
              c6.12,6.12,16.03-6.12,22.15,0L249.77,336C252.7,338.93,256.69,340.58,260.84,340.58z"/>
            <path fill="#FFFFFF" d="M343.17,259.89l-1.13,1.13l-31.09,31.09c-1.9,1.9-4.16,3.21-6.6,3.92c-3.92,1.19-8.2,0.83-11.83-1.13
              c-0.89-0.54-1.72-1.07-2.56-1.72c-0.42-0.3-0.83-0.65-1.25-1.07l-30.02-30.08h-0.06c-2.91-2.91-6.9-4.58-11.06-4.58h-86.71
              c-2.62,0-5.11,0.65-7.37,1.84c-0.89,0.53-1.72,1.07-2.5,1.72c-0.06-0.06-0.06-0.06-0.06,0c-0.36,0.36-0.71,0.65-1.07,1.01
              l-30.08,30.08h-0.06c-0.36,0.36-0.77,0.71-1.19,1.07c-0.77,0.71-1.66,1.25-2.56,1.72c-3.63,1.9-7.91,2.32-11.83,1.13
              c-0.95-0.3-1.84-0.71-2.73-1.13c-0.06,0-0.06,0-0.06,0c-1.31-0.77-2.56-1.66-3.69-2.79l-30.08-30.08h-0.06
              c-0.71-0.77-1.49-1.37-2.32-1.96c-0.06,0.06-0.06,0.06-0.06,0c-1.31-0.89-2.68-1.55-4.16-1.96
              c-1.49-0.42-3.03-0.65-4.58-0.65H6.3v-17.3h50.12c1.55,0,3.09-0.24,4.58-0.65c1.49-0.42,2.85-1.07,4.16-1.96
              c0-0.06,0-0.06,0.06,0c0.83-0.59,1.61-1.19,2.32-1.96h0.06l30.08-30.08c1.13-1.13,2.38-2.08,3.69-2.79c0,0,0-0.06,0.06,0
              c0.47-0.24,0.95-0.48,1.43-0.65c0.48-0.18,0.95-0.36,1.43-0.48c3.86-1.19,8.08-0.77,11.71,1.13
              c0.89,0.48,1.79,1.01,2.56,1.72c0.42,0.36,0.83,0.71,1.19,1.07h0.06l30.08,30.08c0.36,0.36,0.71,0.65,1.07,1.01
              c0,0.06,0,0.06,0.06,0c0.77,0.65,1.61,1.19,2.5,1.72c2.26,1.19,4.76,1.84,7.37,1.84h86.71c4.16,0,8.14-1.66,11.06-4.58h0.06
              l30.08-30.08c0.36-0.42,0.77-0.77,1.19-1.07c0.83-0.65,1.66-1.25,2.56-1.72c3.63-1.9,7.85-2.32,11.71-1.13
              c0.48,0.12,0.95,0.3,1.43,0.48c1.96,0.71,3.74,1.84,5.29,3.45l32.22,32.22C349.23,243.83,349.23,253.76,343.17,259.89z"/>
            <path fill="#FFFFFF" d="M298.02,406.39l-32.23,32.23c-6.12,6.12-6.12,16.03,0,22.15L298.02,493c6.12,6.12,16.03,6.12,22.15,0
              l30.06-30.06c2.94-2.94,6.92-4.59,11.07-4.59h50.21v-17.32h-50.21c-4.15,0-8.14-1.65-11.07-4.59l-30.06-30.06
              C314.05,400.28,304.13,400.28,298.02,406.39z"/>
          </g>
        </svg>
        <defs>
          <linearGradient id="cevro-logo-gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stop-color="#8B5CF6"/>
            <stop offset="100%" stop-color="#7C3AED"/>
          </linearGradient>
        </defs>
      </svg>
    `;
  }

  /**
   * Set agent info (used for message avatars)
   */
  setAgent(agent: Agent): void {
    // Pass agent info to MessageList for message avatars
    this.messageList.setAgent(agent);
  }

  /**
   * Create input area element
   */
  private createInputArea(): HTMLElement {
    const wrapper = createElement('div', {
      className: 'cevro-chat-window__input-wrapper',
    });

    const inputArea = createElement('div', {
      className: 'cevro-chat-window__input-area',
    });

    // File upload button (paperclip icon)
    this.fileButton = createElement('button', {
      className: 'cevro-chat-window__file-button',
      'aria-label': 'Attach file',
      'data-testid': 'file-button',
    }) as HTMLButtonElement;
    this.fileButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.5 6V17.5C16.5 19.71 14.71 21.5 12.5 21.5C10.29 21.5 8.5 19.71 8.5 17.5V5C8.5 3.62 9.62 2.5 11 2.5C12.38 2.5 13.5 3.62 13.5 5V16.5C13.5 17.05 13.05 17.5 12.5 17.5C11.95 17.5 11.5 17.05 11.5 16.5V6H10V16.5C10 17.88 11.12 19 12.5 19C13.88 19 15 17.88 15 16.5V5C15 2.79 13.21 1 11 1C8.79 1 7 2.79 7 5V17.5C7 20.54 9.46 23 12.5 23C15.54 23 18 20.54 18 17.5V6H16.5Z" fill="currentColor"/>
      </svg>
    `;

    // Hidden file input
    this.fileInput = createElement('input', {
      className: 'cevro-chat-window__file-input',
    }) as HTMLInputElement;
    this.fileInput.type = 'file';
    this.fileInput.accept = generateAcceptAttribute();
    this.fileInput.addEventListener('change', () => {
      if (this.fileInput?.files?.length && this.callbacks.onFileUpload) {
        this.callbacks.onFileUpload(this.fileInput.files[0]);
        this.fileInput.value = '';
      }
    });

    this.fileButton.addEventListener('click', () => {
      this.fileInput?.click();
    });

    inputArea.appendChild(this.fileButton);
    inputArea.appendChild(this.fileInput);

    // Text input (max 2000 characters)
    this.inputElement = createElement('textarea', {
      className: 'cevro-chat-window__input',
      'data-testid': 'chat-input',
    }) as HTMLTextAreaElement;
    this.inputElement.placeholder = this.strings.inputPlaceholder;
    this.inputElement.rows = 1;
    this.inputElement.setAttribute('maxlength', '2000');

    // Auto-resize textarea
    this.inputElement.addEventListener('input', () => {
      this.resizeInput();
      this.updateSendButtonState();
    });

    // Handle enter key
    this.inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });

    inputArea.appendChild(this.inputElement);

    // Send button - arrow icon
    this.sendButton = createElement('button', {
      className: 'cevro-chat-window__send-button',
      'aria-label': 'Send message',
      'data-testid': 'send-button',
    }) as HTMLButtonElement;
    this.sendButton.disabled = true;
    this.sendButton.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 19V5M5 12L12 5L19 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    this.sendButton.addEventListener('click', () => {
      this.handleSend();
    });

    inputArea.appendChild(this.sendButton);
    wrapper.appendChild(inputArea);

    // Powered by footer
    const footer = createElement('div', {
      className: 'cevro-chat-window__footer',
    });
    footer.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;
    wrapper.appendChild(footer);

    return wrapper;
  }

  /**
   * Handle send message
   */
  private handleSend(): void {
    if (!this.inputElement) return;

    const message = this.inputElement.value.trim();
    if (!message) return;

    this.callbacks.onSendMessage(message);
    this.inputElement.value = '';
    this.resizeInput();
    this.updateSendButtonState();
    this.inputElement.focus({ preventScroll: true });
  }

  /**
   * Update send button state
   */
  private updateSendButtonState(): void {
    if (this.sendButton && this.inputElement) {
      this.sendButton.disabled = !this.inputElement.value.trim();
    }
  }

  /**
   * Resize textarea to fit its content, capped at 100px
   */
  private resizeInput(): void {
    if (!this.inputElement) return;
    this.inputElement.style.height = 'auto';
    this.inputElement.style.height = Math.min(this.inputElement.scrollHeight, 100) + 'px';
  }

  /**
   * Set draft message in the textarea without sending
   */
  setDraft(message: string): void {
    if (!this.inputElement) return;
    const maxLength = Number(this.inputElement.getAttribute('maxlength')) || 2000;
    this.inputElement.value = message.slice(0, maxLength);
    this.resizeInput();
    this.updateSendButtonState();
    this.inputElement.focus({ preventScroll: true });
  }

  /**
   * Set callback for when user scrolls to the top (for loading older messages)
   */
  setScrollTopHandler(handler: (() => boolean | void | Promise<boolean | void>) | null): void {
    this.messageList.setScrollTopHandler(handler);
  }

  /**
   * Prepend a message at the top (for loading older messages)
   */
  prependMessage(message: Message): void {
    this.messageList.prependMessage(message);
  }

  /**
   * Prepend multiple messages at the top (for loading older messages)
   */
  prependMessages(messages: Message[]): void {
    this.messageList.prependMessages(messages);
  }

  /**
   * Signal that top-loading is complete
   */
  finishLoadingTop(): void {
    this.messageList.finishLoadingTop();
  }

  /**
   * Add message to the chat
   */
  addMessage(message: Message, animate = true): void {
    this.messageList.addMessage(message, animate);
  }

  /**
   * Update message
   */
  updateMessage(message: Message, oldId?: string): void {
    this.messageList.updateMessage(message, oldId);
  }

  /**
   * Update upload progress for a message
   */
  updateMessageProgress(messageId: string, progress: number): void {
    this.messageList.updateMessageProgress(messageId, progress);
  }

  /**
   * Remove a message from the chat
   */
  removeMessage(messageId: string): void {
    this.messageList.removeMessage(messageId);
  }

  /**
   * Show typing indicator
   */
  showTypingIndicator(): void {
    this.messageList.showTypingIndicator();
  }

  /**
   * Hide typing indicator
   */
  hideTypingIndicator(): void {
    this.messageList.hideTypingIndicator();
  }

  /**
   * Clear messages
   */
  clearMessages(): void {
    this.messageList.clear();
  }

  /**
   * Show the chat window
   */
  show(): void {
    if (this.element) {
      this.element.classList.add('cevro-chat-window--visible');
      this.isVisible = true;
      this.inputElement?.focus({ preventScroll: true });
      this.messageList.scrollToBottom();
    }
  }

  /**
   * Hide the chat window
   */
  hide(): void {
    if (this.element) {
      this.element.classList.remove('cevro-chat-window--visible');
      this.isVisible = false;
    }
  }

  /**
   * Check if visible
   */
  isWindowVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Focus input
   */
  focusInput(): void {
    this.inputElement?.focus({ preventScroll: true });
  }

  /**
   * Set ticket closed state
   * Shows CSAT form inline (replacing input area), preserving message history
   *
   * @param closed - Whether the ticket is closed
   * @param onStartNewChat - Callback when user clicks "New Conversation"
   * @param options - Additional options
   * @param options.showHistory - Show message history (always true now for better UX)
   * @param options.skipCSAT - Skip CSAT and show new chat directly
   * @param options.ticketId - Ticket ID for CSAT localStorage tracking
   */
  setTicketClosed(
    closed: boolean,
    onStartNewChat?: () => void,
    options?: { showHistory?: boolean; skipCSAT?: boolean; ticketId?: string }
  ): void {
    const skipCSAT = options?.skipCSAT ?? false;
    const ticketId = options?.ticketId;

    if (closed) {
      // Clean up any existing closed UI first
      this.removeClosedUI();

      // IMPORTANT: Keep message history visible so users can see what they missed
      // Show "Chat closed" message in the message list
      this.messageList.showClosedMessage(this.strings.closedMessage);

      // Hide input area (will be replaced by CSAT or new chat button)
      if (this.inputWrapper) {
        this.inputWrapper.style.display = 'none';
      }

      // Check if CSAT already submitted for this ticket
      const csatSubmitted = ticketId && localStorage.getItem(`cevro_csat_${ticketId}`);

      if (skipCSAT || csatSubmitted) {
        this.showClosedFooter(onStartNewChat);
      } else {
        this.showInlineCSATForm(onStartNewChat, ticketId);
      }
    } else {
      this.removeClosedUI();

      // Show input area — unless pre-chat form is currently displayed
      // (during startNewChat from history, input must stay hidden behind pre-chat)
      if (this.inputWrapper && !this.preChatContainer) {
        this.inputWrapper.style.display = '';
      }

      // Hide closed message
      this.messageList.hideClosedMessage();
    }
  }

  private showInlineCSATForm(onStartNewChat?: () => void, ticketId?: string): void {
    this.removeClosedUI();
    this.csatRating = 0;
    this.csatFeedback = '';

    if (!this.element) return;

    this.csatContainer = createElement('div', {
      className: 'cevro-csat-inline',
    });

    const headerRow = createElement('div', {
      className: 'cevro-csat-inline__header',
    });

    const title = createElement('span', {
      className: 'cevro-csat-inline__title',
    }, [this.strings.csatTitle]);

    const skipButton = createElement('button', {
      className: 'cevro-csat-inline__skip',
      'aria-label': 'Skip feedback',
    }, ['Skip']);
    skipButton.addEventListener('click', () => {
      // Save skip to localStorage so it persists across page reloads
      if (ticketId) {
        localStorage.setItem(`cevro_csat_${ticketId}`, 'skipped');
      }
      this.showClosedFooter(onStartNewChat);
    });

    headerRow.appendChild(title);
    headerRow.appendChild(skipButton);

    const starsRow = createElement('div', {
      className: 'cevro-csat-inline__stars',
    });

    for (let i = 1; i <= 5; i++) {
      const star = createElement('button', {
        className: 'cevro-csat-inline__star',
        'data-rating': String(i),
        'aria-label': `Rate ${i} stars`,
      });
      star.innerHTML = this.createStarSvg(false);
      star.addEventListener('click', () => this.selectInlineRating(i, starsRow));
      star.addEventListener('mouseenter', () => this.previewRating(i, starsRow));
      star.addEventListener('mouseleave', () => this.previewRating(this.csatRating, starsRow));
      starsRow.appendChild(star);
    }

    // Feedback textarea - shown immediately
    const feedbackInput = createElement('textarea', {
      className: 'cevro-csat-inline__feedback',
      placeholder: this.strings.csatPlaceholder,
    }) as HTMLTextAreaElement;
    feedbackInput.rows = 2;
    feedbackInput.addEventListener('input', () => {
      this.csatFeedback = feedbackInput.value;
    });

    // Submit button
    const submitButton = createElement('button', {
      className: 'cevro-csat-inline__submit',
    }, [this.strings.csatSubmit || 'Submit']);
    submitButton.addEventListener('click', () => this.submitInlineCSAT(onStartNewChat, ticketId));

    const poweredBy = createElement('div', {
      className: 'cevro-csat-inline__powered-by',
    });
    poweredBy.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

    this.csatContainer.appendChild(headerRow);
    this.csatContainer.appendChild(starsRow);
    this.csatContainer.appendChild(feedbackInput);
    this.csatContainer.appendChild(submitButton);
    this.csatContainer.appendChild(poweredBy);

    this.element.appendChild(this.csatContainer);
  }

  private selectInlineRating(rating: number, container: HTMLElement): void {
    if (this.csatSubmitting) return;
    this.csatRating = rating;
    this.updateStars(rating, container);

    const feedbackInput = container.parentElement?.querySelector('.cevro-csat-inline__feedback') as HTMLTextAreaElement;
    if (feedbackInput) {
      if (rating <= 2) {
        feedbackInput.placeholder = this.strings.csatPlaceholderNegative || this.strings.csatPlaceholder;
      } else if (rating === 3) {
        feedbackInput.placeholder = this.strings.csatPlaceholderNeutral || this.strings.csatPlaceholder;
      } else {
        feedbackInput.placeholder = this.strings.csatPlaceholder;
      }
    }
  }

  private async submitInlineCSAT(onStartNewChat?: () => void, ticketId?: string): Promise<void> {
    if (this.csatSubmitting || this.csatRating === 0) return;

    this.csatSubmitting = true;

    const submitButton = this.csatContainer?.querySelector('.cevro-csat-inline__submit') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
    }

    try {
      if (this.callbacks.onCSATSubmit) {
        await this.callbacks.onCSATSubmit(this.csatRating, this.csatFeedback || undefined);
      }

      if (ticketId) {
        localStorage.setItem(`cevro_csat_${ticketId}`, '1');
      }

      this.showInlineThankYou(onStartNewChat);
    } catch {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = this.strings.csatSubmit || 'Submit';
      }
    } finally {
      this.csatSubmitting = false;
    }
  }

  /**
   * Show inline thank you message
   */
  private showInlineThankYou(onStartNewChat?: () => void): void {
    this.removeClosedUI();

    if (!this.element) return;

    this.csatContainer = createElement('div', {
      className: 'cevro-csat-inline cevro-csat-inline--thank-you',
    });

    // Thank you row
    const thankYouRow = createElement('div', {
      className: 'cevro-csat-inline__thank-you-row',
    });

    const checkIcon = createElement('span', {
      className: 'cevro-csat-inline__check',
    });
    checkIcon.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="12" fill="#10B981"/>
        <path d="M7 12L10 15L17 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    const message = createElement('span', {
      className: 'cevro-csat-inline__message',
    }, [this.strings.csatThankYou]);

    thankYouRow.appendChild(checkIcon);
    thankYouRow.appendChild(message);

    // New conversation button
    const newChatButton = createElement('button', {
      className: 'cevro-csat-inline__new-chat',
    }, [this.strings.newConversation]);

    newChatButton.addEventListener('click', () => {
      if (onStartNewChat) {
        onStartNewChat();
      }
    });

    // Powered by footer
    const poweredBy = createElement('div', {
      className: 'cevro-csat-inline__powered-by',
    });
    poweredBy.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

    this.csatContainer.appendChild(thankYouRow);
    this.csatContainer.appendChild(newChatButton);
    this.csatContainer.appendChild(poweredBy);

    this.element.appendChild(this.csatContainer);
  }

  /**
   * Show closed footer with new conversation button (after CSAT skip or when CSAT already submitted)
   */
  private showClosedFooter(onStartNewChat?: () => void): void {
    this.removeClosedUI();

    if (this.element) {
      this.closedFooter = createElement('div', {
        className: 'cevro-chat-window__closed-footer',
      });

      const newChatButton = createElement('button', {
        className: 'cevro-chat-window__new-chat-button',
      }, [this.strings.newConversation]);

      newChatButton.addEventListener('click', () => {
        if (onStartNewChat) {
          onStartNewChat();
        }
      });

      const poweredBy = createElement('div', {
        className: 'cevro-chat-window__powered-by',
      });
      poweredBy.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

      this.closedFooter.appendChild(newChatButton);
      this.closedFooter.appendChild(poweredBy);
      this.element.appendChild(this.closedFooter);
    }
  }

  /**
   * Remove all closed-state UI (CSAT form, thank-you, closed footer)
   */
  private removeClosedUI(): void {
    if (this.csatContainer && this.csatContainer.parentNode) {
      this.csatContainer.parentNode.removeChild(this.csatContainer);
      this.csatContainer = null;
    }
    if (this.closedFooter && this.closedFooter.parentNode) {
      this.closedFooter.parentNode.removeChild(this.closedFooter);
      this.closedFooter = null;
    }
  }

  /**
   * Preview star rating on hover
   */
  private previewRating(rating: number, container: HTMLElement): void {
    this.updateStars(rating, container);
  }

  /**
   * Update stars display (works for both old and inline CSAT)
   */
  private updateStars(rating: number, container: HTMLElement): void {
    // Support both old and new star classes
    const stars = container.querySelectorAll('.cevro-csat__star, .cevro-csat-inline__star');
    stars.forEach((star, index) => {
      const filled = index < rating;
      star.innerHTML = this.createStarSvg(filled);
      star.classList.toggle('cevro-csat__star--filled', filled);
      star.classList.toggle('cevro-csat-inline__star--filled', filled);
    });
  }

  /**
   * Create star SVG
   */
  private createStarSvg(filled: boolean): string {
    if (filled) {
      return `
        <svg width="32" height="32" viewBox="0 0 24 24" fill="#FBBF24" stroke="#FBBF24" stroke-width="1">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      `;
    }
    return `
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" stroke-width="1.5">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    `;
  }

  /**
   * Show pre-chat form for visitors
   */
  showPreChatForm(): void {
    if (this.preChatCompleted || this.preChatContainer) return;
    if (!this.element) return;

    // Add compact mode class for smaller modal
    this.element.classList.add('cevro-chat-window--prechat');

    // Hide messages and input
    const messagesArea = this.element.querySelector('.cevro-messages');
    if (messagesArea) {
      (messagesArea as HTMLElement).style.display = 'none';
    }
    if (this.inputWrapper) {
      this.inputWrapper.style.display = 'none';
    }

    // Create pre-chat form
    this.preChatContainer = createElement('div', {
      className: 'cevro-prechat',
    });

    // Title
    const title = createElement('h3', {
      className: 'cevro-prechat__title',
    }, [this.strings.preChatTitle]);

    // Subtitle
    const subtitle = createElement('p', {
      className: 'cevro-prechat__subtitle',
    }, [this.strings.preChatSubtitle]);

    // Form
    const form = createElement('form', {
      className: 'cevro-prechat__form',
    });

    // First name field
    const firstNameGroup = createElement('div', {
      className: 'cevro-prechat__field',
    });
    const firstNameLabel = createElement('label', {
      className: 'cevro-prechat__label',
      for: 'cevro-prechat-firstname',
    }, [this.strings.preChatFirstNameLabel]);
    const firstNameInput = createElement('input', {
      className: 'cevro-prechat__input',
      id: 'cevro-prechat-firstname',
      type: 'text',
      'data-testid': 'prechat-firstname',
    }) as HTMLInputElement;
    firstNameInput.placeholder = this.strings.preChatFirstNamePlaceholder;
    const firstNameError = createElement('span', {
      className: 'cevro-prechat__error',
      'data-field': 'firstName',
    });

    // Highlight invalid fields on blur (no text — avoids extra localization)
    firstNameInput.addEventListener('blur', () => {
      firstNameInput.classList.toggle('cevro-prechat__input--error', !firstNameInput.value.trim());
    });

    firstNameGroup.appendChild(firstNameLabel);
    firstNameGroup.appendChild(firstNameInput);
    firstNameGroup.appendChild(firstNameError);

    // Email field
    const emailGroup = createElement('div', {
      className: 'cevro-prechat__field',
    });
    const emailLabel = createElement('label', {
      className: 'cevro-prechat__label',
      for: 'cevro-prechat-email',
    }, [this.strings.preChatEmailLabel]);
    const emailInput = createElement('input', {
      className: 'cevro-prechat__input',
      id: 'cevro-prechat-email',
      type: 'text',
      autocomplete: 'email',
      'data-testid': 'prechat-email',
    }) as HTMLInputElement;
    emailInput.placeholder = this.strings.preChatEmailPlaceholder;
    const emailError = createElement('span', {
      className: 'cevro-prechat__error',
      'data-field': 'email',
    });

    // Highlight invalid fields on blur (no text — avoids extra localization)
    emailInput.addEventListener('blur', () => {
      const email = emailInput.value.trim();
      emailInput.classList.toggle('cevro-prechat__input--error', !email || !this.isValidEmail(email));
    });

    emailGroup.appendChild(emailLabel);
    emailGroup.appendChild(emailInput);
    emailGroup.appendChild(emailError);

    // Submit button
    const submitButton = createElement('button', {
      className: 'cevro-prechat__submit',
      type: 'submit',
      'data-testid': 'prechat-submit',
    }, [this.strings.preChatSubmit]) as HTMLButtonElement;

    // Form validation and submit
    form.addEventListener('submit', (e) => {
      e.preventDefault();

      // Clear previous errors
      firstNameError.textContent = '';
      emailError.textContent = '';
      firstNameInput.classList.remove('cevro-prechat__input--error');
      emailInput.classList.remove('cevro-prechat__input--error');

      const firstName = firstNameInput.value.trim();
      const email = emailInput.value.trim();

      let hasError = false;

      if (!firstName) {
        firstNameError.textContent = this.strings.preChatFirstNameRequired;
        firstNameInput.classList.add('cevro-prechat__input--error');
        hasError = true;
      }

      if (!email) {
        emailError.textContent = this.strings.preChatEmailRequired;
        emailInput.classList.add('cevro-prechat__input--error');
        hasError = true;
      } else if (!this.isValidEmail(email)) {
        emailError.textContent = this.strings.preChatEmailInvalid;
        emailInput.classList.add('cevro-prechat__input--error');
        hasError = true;
      }

      if (hasError) return;

      // Submit successful
      this.preChatCompleted = true;

      if (this.callbacks.onPreChatSubmit) {
        this.callbacks.onPreChatSubmit({ firstName, email });
      }
    });

    form.appendChild(firstNameGroup);
    form.appendChild(emailGroup);
    form.appendChild(submitButton);

    // Powered by footer
    const poweredBy = createElement('div', {
      className: 'cevro-prechat__powered-by',
    });
    poweredBy.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

    this.preChatContainer.appendChild(title);
    this.preChatContainer.appendChild(subtitle);
    this.preChatContainer.appendChild(form);
    this.preChatContainer.appendChild(poweredBy);

    this.element.appendChild(this.preChatContainer);

    // Focus on name input
    firstNameInput.focus({ preventScroll: true });
  }

  /**
   * Hide pre-chat form and show chat
   */
  hidePreChatForm(): void {
    if (this.preChatContainer && this.preChatContainer.parentNode) {
      this.preChatContainer.parentNode.removeChild(this.preChatContainer);
      this.preChatContainer = null;
    }

    // Remove compact mode class
    this.element?.classList.remove('cevro-chat-window--prechat');

    // Show messages and input with fade-in
    if (this.element) {
      const messagesArea = this.element.querySelector('.cevro-messages');
      if (messagesArea) {
        (messagesArea as HTMLElement).style.display = '';
        messagesArea.classList.add('cevro-messages--fade-in');
      }
    }
    if (this.inputWrapper) {
      this.inputWrapper.style.display = '';
    }

    // Focus input
    this.inputElement?.focus({ preventScroll: true });
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Set the brand logo shown as the avatar of the conversation's first message only.
   * Distinct from the header logo (`updateCustomLogo`) — callers decide independently
   * whether the first-message avatar should follow the header logo.
   */
  setBrandLogoUrl(logoUrl?: string): void {
    this.messageList.setBrandLogoUrl(logoUrl);
  }

  updateCustomLogo(logoUrl?: string): void {
    this.customLogoUrl = logoUrl;

    if (!this.logoElement) return;

    // Clear existing logo content
    this.logoElement.innerHTML = '';

    if (logoUrl) {
      const logoImg = document.createElement('img');
      logoImg.src = logoUrl;
      logoImg.alt = 'Logo';
      logoImg.className = 'cevro-chat-window__logo-img';
      logoImg.style.width = '40px';
      logoImg.style.height = '40px';
      logoImg.style.objectFit = 'contain';
      logoImg.style.borderRadius = '8px';
      this.logoElement.appendChild(logoImg);
    } else {
      this.logoElement.innerHTML = this.createCompanyLogoSvg();
    }
  }

  /**
   * Get the body of the last message (for conversation list preview)
   */
  getLastMessageBody(): string | undefined {
    return this.messageList.getLastMessageBody();
  }

  getLastMessageAt(): string | undefined {
    return this.messageList.getLastMessageAt();
  }

  getAgentMessagesSinceLastUserMessage(): number {
    return this.messageList.getAgentMessagesSinceLastUserMessage();
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.messageList.destroy();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.inputElement = null;
    this.sendButton = null;
    this.fileInput = null;
    this.fileButton = null;
    this.closedFooter = null;
    this.csatContainer = null;
    this.preChatContainer = null;
    this.preChatCompleted = false;
  }
}
